import { Component, h, RefObject, createRef } from 'preact';
import { Subject, Subscription } from 'rxjs';
import cx from 'classnames';

import { ReqState } from '../../../../../types/request-state.model';

import { ChoicesButtonGroup } from '../../common/choices-button-group.component';
import { ToggleSwitch } from '../../common/toggle-switch.component';
import { DivCat } from '../../../../../types/div-cat.model';
import { InquiryDocument } from 'inquiries/inquiry-document.model';

import { dateFormatter, formatWithTimezone } from '../../../../core/date-formatter';
import { FurnishingChoices } from '../../../../../constants';
import { FormattedNumberInput } from '../../common/formatted-number-input.component';

import { Form, FieldList, Field } from '../../../../utils/forms/form';
import * as Validators from '../../../../utils/forms/validators';
import { cxValidateField } from '../../../../utils/forms/validators';
import { handlers } from '../../../../core/multi-event-handler';
import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../../../utils/forms/hoppler-linkstate';
import { options } from '../../../../../constants/options';
import { InquiryDocumentType } from '../../../../../constants/inquiries';
import { DocumentValidators } from './document-validators'
import { DatePickerControl } from '../../common/date-picker-control.component';
import { InqDocumentModalTabs, DocumentTabID } from './inq-document-modal-tabs.component';

interface InqDocumentTermsFormProps {
  divCat: DivCat;
  document: InquiryDocument;
  documentType: InquiryDocumentType;
  continuingWithoutProperty: boolean;

  saveState: ReqState;

  activeTab: DocumentTabID;
  onTabClick: (tabId: DocumentTabID) => void;

  onDocumentChange: (document: InquiryDocument) => void;
  onNextClick: () => void;

  onPrintClick: () => void;
  onSaveClick: () => void;
}

interface InqDocumentTermsFormState {
  form?: Form;
}

export class InqDocumentTermsForm extends Component<InqDocumentTermsFormProps, InqDocumentTermsFormState> {
  __linkToDocumentCallbackCache: { [k: string]: (event: Event) => void } = {};
  subscriptions: Subscription[] = [];
  form$ = new Subject<Form>();

  refs: { [k: string]: RefObject<HTMLElement> } = {};
  formScrollerRef = createRef<HTMLDivElement>();

  componentWillMount() {
    this.setupForm();
  }

  setupForm() {
    const props = this.props;
    const doc = props.document;
    const documentType = props.documentType;

    const cwp = props.continuingWithoutProperty;
    const isSale = doc.category === 'Sale';
    const isRent = doc.category === 'Rent';

    const fields: FieldList = {
      // Continue without property fields
      'parkingSpaces': new Field([], [ DocumentValidators.requiredIf(isSale && cwp) ], doc.parkingSpaces),
      'bedrooms': new Field([], [ DocumentValidators.requiredIf(isSale && cwp) ], doc.bedrooms),
      'floorArea': new Field([], [ DocumentValidators.requiredIf(cwp) ], doc.floorArea),
      'furnished': new Field([], [ DocumentValidators.requiredIf(isSale && cwp) ], doc.furnished),
      'propertyAddress': new Field([ Validators.required ], null, doc.propertyAddress),

      'leaseTerm': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.leaseTerm),
      'leaseType': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.leaseType),

      'leaseStartDate': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.leaseStartDate),
      'leaseEndDate': new Field([], [], doc.leaseEndDate), // Autocalculated, do not validate
      
      'loiValidityDate': new Field(
        [],
        documentType === InquiryDocumentType.OFFER ? [ DocumentValidators.requiredForRent(doc) ] : [],
        doc.loiValidityDate
      ),
      'isInclusiveOfDues': new Field([], null, doc.isInclusiveOfDues),
      'vatIncluded': new Field([], null, doc.vatIncluded),
      'wTaxIncluded': new Field([], null, doc.wTaxIncluded),

      'monthlyRent': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.monthlyRent),
      'securityDeposit': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.securityDeposit),
      'advancePayment': new Field([], [ DocumentValidators.requiredForRent(doc) ], doc.advancePayment),
      'commission': new Field(
        [],
        [ DocumentValidators.requiredIf(doc.category === 'Rent' && documentType === InquiryDocumentType.OFFER) ],
        doc.commission
      ),

      'salePrice': new Field([], [ DocumentValidators.requiredForSale(doc) ], doc.salePrice),
      'discountedPercent': new Field([], options.discountEnabled ? [ DocumentValidators.requiredForSale(doc) ] : null, doc.discountedPercent),
      'downpayment': new Field([], [ DocumentValidators.requiredForSale(doc) ], doc.downpayment),
      'balancePaidOffIn': new Field([], [ DocumentValidators.requiredForSale(doc) ], doc.balancePaidOffIn),
      'brokersFee': new Field([], [ DocumentValidators.requiredForSale(doc) ], doc.brokersFee),

      'additionalPaymentTerms': new Field([]),

      'registryOfDeeds': new Field([], [ DocumentValidators.reqContractSale(doc, documentType) ], doc.registryOfDeeds),
      'registryOfDeedsLocation': new Field([], [ DocumentValidators.reqContractSale(doc, documentType) ], doc.registryOfDeedsLocation),
      'signatureDate': new Field([], [ DocumentValidators.reqContractSale(doc, documentType) ], doc.signatureDate),
      'signatureLocation': new Field([], [ DocumentValidators.reqContractSale(doc, documentType) ], doc.signatureLocation),

      'repairMinAmount': new Field([], [ DocumentValidators.reqContractRent(doc, documentType) ], doc.repairMinAmount),
      'noticeToRenew': new Field([], [ DocumentValidators.reqContractRent(doc, documentType) ], doc.noticeToRenew),
      'securityDepositReturn': new Field([], [ DocumentValidators.reqContractRent(doc, documentType) ], doc.securityDepositReturn),
      'propertyManagementOffice': new Field([], [ DocumentValidators.reqContractRent(doc, documentType) ], doc.propertyManagementOffice),
      'courtHandlingDisputes': new Field([], [ DocumentValidators.reqContractRent(doc, documentType) ], doc.courtHandlingDisputes),
    };
    const form = new Form(fields, { pristineUntilEdited: true });
    Validators.validateForm(form);

    this.state = { form };
    this.subscriptions.push( Form.bind(this, this.form$) );

    for (let key in fields) {
      this.refs[key] = createRef();
    }
  }

  linkToDocument(field: string, transformFunction?: (value: any) => any) {
    let callback: (event: Event) => void;
    if (this.__linkToDocumentCallbackCache[field]) {
      // console.log('Reusing linkToDocument callback for', field);
      callback = this.__linkToDocumentCallbackCache[field];
    } else {
      // console.log('Created linkToDocument callback for', field);
      callback = this.__linkToDocumentCallbackCache[field] = event => {
        const target = event.target as HTMLInputElement;
        let value = target.value;

        if (transformFunction) {
          value = transformFunction(value)
        }

        const newDoc = { ...this.props.document, [field]: value };
        this.props.onDocumentChange(newDoc);
      }
    }

    return callback;
  }

  onDirectValueChange(value: any, field: string) {
    const newDoc = { ...this.props.document, [field]: value };
    this.props.onDocumentChange(newDoc);

    hopplerUpdateFormFieldValue(this.form$, this.state.form, field, value);
  }

  onNextClick() {
    const form = this.state.form;
    Validators.touchEntireForm(this.form$, form);

    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      this.props.onNextClick();
    }
  }

  onSaveClick() {
    const form = this.state.form;
    Validators.touchEntireForm(this.form$, form);

    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      this.props.onSaveClick();
    }
  }

  onTabClick(tabId: DocumentTabID) {
    Validators.touchEntireForm(this.form$, this.state.form);
    const form = this.state.form;
    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      this.props.onTabClick(tabId);
    }
  }

  onPrintClick() {
    const form = this.state.form;
    Validators.touchEntireForm(this.form$, form);

    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      this.props.onPrintClick(); 
    }
  }

  onValidationScroll(offsetTop: number) {
    // console.log('offsetTop:', offsetTop);
    const scroller = this.formScrollerRef.current;
    const scrollerRect = scroller.getBoundingClientRect();
    // 64px extra vertical space to allow for input labels and spacing
    const targetScroll = offsetTop - scrollerRect.top - 64;

    scroller.scrollTo({ top: targetScroll, left: 0, behavior: 'smooth' });
  }

  scrollToFirstError() {
    const form = this.state.form;
    const firstInvalidKey = form.getFirstInvalidFieldName();

    if (firstInvalidKey) {
      const ref = this.refs[firstInvalidKey];
      let current = ref.current;
      // console.log('First invalid, current:', firstInvalidKey, current);

      // If the element is a Preact component, we need to reach inside and get
      // the actual DOM element
      if (current['base']) {
        current = current['base'];
      }

      this.onValidationScroll(current.offsetTop);
    }
  }


  getDatesSection() {
    const { divCat, documentType, document } = this.props;
    const form = this.state.form;
    const form$ = this.form$;

    if (divCat.category === 'Rent' || documentType === InquiryDocumentType.OFFER) {
      return (
        <div className="hi-doctermsform__dates-group">
          <div className="hi-doctermsform__lease-term">
            <label className="hi-input-group__label">Lease Term (# of Months)</label>
            <input type="number" min="1" placeholder="0"
              value={ document.leaseTerm }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.leaseTerm)
              }) }
              onChange={ handlers(
                this.linkToDocument('leaseTerm'),
                hopplerLinkState(form$, form, 'leaseTerm')
              ) }
              ref={ this.refs.leaseTerm } />
          </div>
  
          <div className="hi-doctermsform__lease-type">
            <label className="hi-input-group__label">Lease Type</label>
            <ChoicesButtonGroup choices={ ['Personal', 'Company'] }
                selectedChoice={ document.leaseType }
                valid={ !cxValidateField(form.fields.leaseType) }
                onSelect={ choice => this.onDirectValueChange(choice, 'leaseType') }
                ref={ this.refs.leaseType } />
          </div>
  
          <div className="hi-doctermsform__lease-date">
            <label className="hi-input-group__label">Lease Date (From - To)</label>
            <div className="d-flex">
              <DatePickerControl placeholder="From"
                value={ dateFormatter.toNativeInputDateFormat(document.leaseStartDate) }
                className={ cx('form-control hi-doctermsform__lease-date-input', {
                  'is-invalid': cxValidateField(form.fields.leaseStartDate)
                }) }
                onChange={ handlers(
                  this.linkToDocument('leaseStartDate', formatWithTimezone),
                  hopplerLinkState(form$, form, 'leaseStartDate')
                ) }
                ref={ this.refs.leaseStartDate } />
              <DatePickerControl placeholder="To"
                disabled
                className={ cx('form-control hi-doctermsform__lease-date-input', {
                  // Yup, use validation for *start* date, because this field is disabled and read-only
                  'is-invalid': cxValidateField(form.fields.leaseStartDate)
                }) }
                value={ dateFormatter.toNativeInputDateFormat(document.leaseEndDate) } />
            </div>
          </div>
  
          {/* LOI Validity must be included on both Rent and Sale LOIs */}
          { documentType === InquiryDocumentType.OFFER && (
            <div className="hi-doctermsform__loi-validity">
              <label className="hi-input-group__label">LOI Valid Until</label>
              <DatePickerControl placeholder="MM/DD/YYYY"
                value={ dateFormatter.toNativeInputDateFormat(document.loiValidityDate) }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.loiValidityDate)
                }) }
                onChange={ handlers(
                  this.linkToDocument('loiValidityDate', formatWithTimezone),
                  hopplerLinkState(form$, form, 'loiValidityDate')
                ) }
                ref={ this.refs.loiValidityDate } />
            </div>
          )}
        </div>
      )
    }
  }

  getCwpResidentialGroup(props: InqDocumentTermsFormProps, state: InqDocumentTermsFormState) {
    const document = props.document;
    const form = state.form;
    const form$ = this.form$;

    if (props.continuingWithoutProperty) {
      return (
        <div className="hi-doctermsform__cwp-residential-group">
          <div className="hi-doctermsform__cwp-parking hi-doctermsform__sale-only">
            <label className="hi-input-group__label">Parking Spaces</label>
            <input type="number" min="0" placeholder="0"
              value={ document.parkingSpaces }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.parkingSpaces)
              }) }
              onChange={ handlers(
                this.linkToDocument('parkingSpaces', parseInt),
                hopplerLinkState(form$, form, 'parkingSpaces')
              ) }
              ref={ this.refs.parkingSpaces } />
          </div>

          <div className="hi-doctermsform__cwp-bedrooms hi-doctermsform__sale-only">
            <label className="hi-input-group__label">Number of Bedrooms</label>
            <input type="number" min="0" placeholder="0"
              value={ document.bedrooms }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.bedrooms)
              }) }
              onChange={ handlers(
                this.linkToDocument('bedrooms', parseInt),
                hopplerLinkState(form$, form, 'bedrooms')
              ) }
              ref={ this.refs.bedrooms } />
          </div>

          <div className="hi-doctermsform__cwp-floor-area">
            <label className="hi-input-group__label">Floor Area (sqm)</label>
            <input type="number" min="0" placeholder="0"
              value={ document.floorArea }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.floorArea)
              }) }
              onChange={ handlers(
                this.linkToDocument('floorArea', parseFloat),
                hopplerLinkState(form$, form, 'floorArea')
              ) }
              ref={ this.refs.floorArea } />
          </div>

          <div className="hi-doctermsform__cwp-furnishing hi-doctermsform__sale-only">
            <label className="hi-input-group__label">Furnishing</label>
            <select value={ document.furnished }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.furnished)
              }) }
              onChange={ handlers(
                this.linkToDocument('furnished'),
                hopplerLinkState(form$, form, 'furnished')
              ) }
              ref={ this.refs.furnished }>
              { FurnishingChoices.map(choice => (
                <option value={ choice.value }>{ choice.label }</option>
              )) }
            </select>
          </div>
        </div>
      );
    }
  }

  renderForm(props: InqDocumentTermsFormProps, state: InqDocumentTermsFormState) {
    const document = props.document || {};
    const { divCat, documentType } = this.props;
    const form$ = this.form$, form = this.state.form;

    return (
      <div className={ cx('hi-doctermsform', {
        'hi-doctermsform--rent': divCat.category === 'Rent',
        'hi-doctermsform--sale': divCat.category === 'Sale',
        'hi-doctermsform--residential': divCat.division === 'Residential',
        'hi-doctermsform--commercial': divCat.division === 'Commercial'
      }) }>
        { this.getCwpResidentialGroup(props, state) }

        <div className="hi-doctermsform__cwp-property-address">
          <label className="hi-input-group__label">Property Address</label>
          <input type="text"
            value={ document.propertyAddress }
            disabled={ !!document.propertyKey }
            className={ cx('form-control', {
              'is-invalid': cxValidateField(form.fields.propertyAddress)
            }) }
            onChange={ handlers(
              this.linkToDocument('propertyAddress'),
              hopplerLinkState(form$, form, 'propertyAddress')
            ) }
            ref={ this.refs.propertyAddress } />
        </div>

        { this.getDatesSection() }
        
        <div className="hi-doctermsform__inclusives-toggles">
          <div className="hi-doctermsform__dues-included">
            <ToggleSwitch onChange={ value => this.onDirectValueChange(value ? 1 : 0, 'isInclusiveOfDues') }
              value={ !!document.isInclusiveOfDues }
              label='Inclusive of Dues' />
          </div>
          <ToggleSwitch onChange={ value => this.onDirectValueChange(value ? 1 : 0, 'vatIncluded') }
            value={ !!document.vatIncluded }
            label='Inclusive of VAT' />
          <ToggleSwitch onChange={ value => this.onDirectValueChange(value ? 1 : 0, 'wTaxIncluded') }
            value={ !!document.wTaxIncluded }
            label='Inclusive of Withholding Tax' />
        </div>

        {/* RENT */}
        <div className="hi-input-group hi-doctermsform__rent-only">
          <label className="hi-input-group__label">Monthly Rent Price</label>
          <FormattedNumberInput type="number" placeholder="PHP"
            value={ document.monthlyRent } prefix='PHP '
            className={ cx('form-control', {
              'is-invalid': cxValidateField(form.fields.monthlyRent)
            }) }
            onChange={ handlers(
              this.linkToDocument('monthlyRent'),
              hopplerLinkState(form$, form, 'monthlyRent')
            ) }
            ref={ this.refs.monthlyRent } />
        </div>

        {/* The inputs in this group take in a number of months */}
        <div className="hi-doctermsform__month-fields hi-doctermsform__rent-only">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Security Deposit (# of Months)</label>
            <FormattedNumberInput type="number" placeholder="0"
              value={ document.securityDeposit }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.securityDeposit)
              }) }
              onChange={ handlers(
                this.linkToDocument('securityDeposit', parseFloat),
                hopplerLinkState(form$, form, 'securityDeposit')
              ) }
              ref={ this.refs.securityDeposit } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Security Deposit Amount</label>
            <FormattedNumberInput type="number" className="form-control" placeholder="0" disabled={ true }
              value={ document.securityDepositAmount } prefix='PHP ' />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Advance Payment (# of Months)</label>
            <FormattedNumberInput type="number" placeholder="0"
              value={ document.advancePayment }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.advancePayment)
              }) }
              onChange={ handlers(
                this.linkToDocument('advancePayment', parseFloat),
                hopplerLinkState(form$, form, 'advancePayment')
              ) }
              ref={ this.refs.advancePayment } />
          </div>

          <div className="hi-input-group">
            <label className="hi-input-group__label">Advance Payment Amount</label>
            <FormattedNumberInput type="number" className="form-control" placeholder="0" disabled
              value={ document.advancePaymentAmount } prefix='PHP ' />
          </div>

          { documentType === InquiryDocumentType.OFFER && (
            <div className="hi-input-group">
              <label className="hi-input-group__label">Commission (# of Months)</label>
              <FormattedNumberInput type="number" placeholder="0"
                value={ document.commission }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.commission)
                }) }
                onChange={ handlers(
                  this.linkToDocument('commission', parseFloat),
                  hopplerLinkState(form$, form, 'commission')
                ) }
                ref={ this.refs.commission } />
            </div>
          )}

          { documentType === InquiryDocumentType.OFFER && (
            <div className="hi-input-group">
              <label className="hi-input-group__label">Commission Amount</label>
              <FormattedNumberInput type="number" className="form-control" placeholder="0" disabled
                value={ document.commissionAmount } prefix='PHP ' />
            </div>
          )}
        </div>

        {/* SALE */}
        <div className="hi-doctermsform__sale-price-dp-fields hi-doctermsform__sale-only">
          <div className="hi-input-group hi-doctermsform__sale-price-field">
            <label className="hi-input-group__label">Sale Price</label>
            <FormattedNumberInput type="number" placeholder="PHP"
              value={ document.salePrice } prefix='PHP '
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.salePrice)
              }) }
              onChange={ handlers(
                this.linkToDocument('salePrice', parseFloat),
                hopplerLinkState(form$, form, 'salePrice')
              ) }
              ref={ this.refs.salePrice } />
          </div>

          { options.discountEnabled && (
            <div className="hi-input-group" style="grid-area: discount">
              <label className="hi-input-group__label">Discount Percentage (%)</label>
              <input type="number" placeholder="0"
                min={ 0 } max={ 100 }
                value={ document.discountedPercent }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.discountedPercent)
                }) }
                onChange={ handlers(
                  this.linkToDocument('discountedPercent', parseFloat),
                  hopplerLinkState(form$, form, 'discountedPercent')
                ) }
                ref={ this.refs.discountedPercent } />
            </div>
          )}

          { options.discountEnabled && (
            <div className="hi-input-group" style="grid-area: discount-percent">
              <label className="hi-input-group__label">Discounted Price (PHP)</label>
              <FormattedNumberInput type="number" className="form-control" placeholder="0" disabled
                value={ document.discountedPrice } prefix='PHP ' />
            </div>
          )}

          <div className="hi-input-group hi-doctermsform__downpct-field">
            <label className="hi-input-group__label">Downpayment (in Percentage)</label>
            <input type="number" placeholder="0"
              min='0' max='100'
              value={ document.downpayment }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.downpayment)
              }) }
              onChange={ handlers(
                this.linkToDocument('downpayment', parseFloat),
                hopplerLinkState(form$, form, 'downpayment')
              ) }
              ref={ this.refs.downpayment } />
          </div>

          <div className="hi-input-group hi-doctermsform__downamt-field">
            <label className="hi-input-group__label">Downpayment Amount</label>
            <FormattedNumberInput type="number" className="form-control" placeholder="0" disabled
              value={ document.downpaymentAmount } prefix='PHP ' />
          </div>

          <div className="hi-input-group hi-doctermsform__balance-field">
            <label className="hi-input-group__label">Balance Paid Off In (# of Days)</label>
            <input type="number" placeholder="0"
              min={ 0 }
              value={ document.balancePaidOffIn }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.balancePaidOffIn)
              }) }
              onChange={ handlers(
                this.linkToDocument('balancePaidOffIn', parseInt),
                hopplerLinkState(form$, form, 'balancePaidOffIn')
              ) }
              ref={ this.refs.balancePaidOffIn } />
          </div>

          <div className="hi-input-group hi-doctermsform__brokers-fee-field">
            <label className="hi-input-group__label">Broker's Fee (in Percentage)</label>
            <FormattedNumberInput type="number" placeholder="%"
              min={ 0 } max={ 100 }
              value={ document.brokersFee } suffix='%'
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.brokersFee)
              }) }
              onChange={ handlers(
                this.linkToDocument('brokersFee', parseFloat),
                hopplerLinkState(form$, form, 'brokersFee')
              ) }
              ref={ this.refs.brokersFee } />
          </div>
        </div>
        

        { props.documentType === InquiryDocumentType.OFFER && (
          /* Additional Payment Terms is used in both Rent and Sale LOIs */
          <div className="hi-input-group">
            <label className="hi-input-group__label">Additional Payment Terms</label>
            <textarea 
              placeholder="Write here"
              value={ document.additionalPaymentTerms }
              className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.additionalPaymentTerms)
              }) }
              onChange={ handlers(
                this.linkToDocument('additionalPaymentTerms'),
                hopplerLinkState(form$, form, 'additionalPaymentTerms')
              ) }
              ref={ this.refs.advancePayment } />
          </div>
        )}

        { props.documentType === InquiryDocumentType.CONTRACT && (
          <div className="hi-doctermsform__contract-sale-section hi-doctermsform__sale-only">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Registry of Deeds Details</label>
              <input type="text" placeholder="Registry of deeds details"
                className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.registryOfDeeds)
              }) }
                value={ document.registryOfDeeds }
                onChange={ handlers(
                  this.linkToDocument('registryOfDeeds'),
                  hopplerLinkState(form$, form, 'registryOfDeeds')
                )}
                ref={ this.refs.registryOfDeeds }  />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Registry of Deeds Location</label>

              {/*
                WORKAROUND - Possible Preact 8 bug
                cx('form-control', { ... }) returns the expected values, but
                sometimes does not take effect on "Registry of Deeds Location"
                and "Signature Location".

                Adding another class -- even a dummy one that doesn't exist --
                appears to fix the issue.
              */}
              <input type="text" placeholder="Registry of deeds location"
                className={ cx('hi-doctermsform__workaround-test-0 form-control', {
                  'is-invalid': cxValidateField(form.fields.registryOfDeedsLocation)
                }) }
                value={ document.registryOfDeedsLocation }
                onChange={ handlers(
                  this.linkToDocument('registryOfDeedsLocation'),
                  hopplerLinkState(form$, form, 'registryOfDeedsLocation')
                )}
                ref={ this.refs.registryOfDeedsLocation } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Signature Date</label>
              <DatePickerControl placeholder="Signature date"
                className={ cx('form-control', {
                'is-invalid': cxValidateField(form.fields.signatureDate)
              }) }
                value={ document.signatureDate }
                onChange={ handlers(
                  this.linkToDocument('signatureDate'),
                  hopplerLinkState(form$, form, 'signatureDate')
                )}
                ref={ this.refs.signatureDate } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Signature Location</label>

              {/*
                WORKAROUND - Possible Preact 8 bug
                cx('form-control', { ... }) returns the expected values, but
                sometimes does not take effect on "Registry of Deeds Location"
                and "Signature Location".

                Adding another class -- even a dummy one that doesn't exist --
                appears to fix the issue.
              */}
              <input type="text" placeholder="Signature location"
                className={ cx('hi-doctermsform__workaround-test-0 form-control', {
                  'is-invalid': cxValidateField(form.fields.signatureLocation)
                }) }
                value={ document.signatureLocation }
                onChange={ handlers(
                  this.linkToDocument('signatureLocation'),
                  hopplerLinkState(form$, form, 'signatureLocation')
                )}
                ref={ this.refs.signatureLocation } />
            </div>
          </div>
        )}

        { props.documentType === InquiryDocumentType.CONTRACT && (
          <div className="hi-doctermsform__contract-rent-section hi-doctermsform__rent-only">
            <div className="hi-input-group hi-doctermsform__contract-min-repair-value">
              <label className="hi-input-group__label">Min. Value of Repairs to be Covered by Lessor</label>
              <FormattedNumberInput placeholder="Min. repair value" prefix="PHP "
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.repairMinAmount)
                })}
                value={ document.repairMinAmount }
                onChange={ handlers(
                  this.linkToDocument('repairMinAmount'),
                  hopplerLinkState(form$, form, 'repairMinAmount')
                )}
                ref={ this.refs.repairMinAmount } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Notice to Renew Contract (# of Days)</label>
              <FormattedNumberInput placeholder="# days"
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.noticeToRenew)
                })}
                value={ document.noticeToRenew }
                onChange={ handlers(
                  this.linkToDocument('noticeToRenew'),
                  hopplerLinkState(form$, form, 'noticeToRenew')
                )}
                ref={ this.refs.noticeToRenew } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Security Deposit Return (# of Days)</label>
              <FormattedNumberInput placeholder="# days"
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.securityDepositReturn)
                })}
                value={ document.securityDepositReturn }
                onChange={ handlers(
                  this.linkToDocument('securityDepositReturn'),
                  hopplerLinkState(form$, form, 'securityDepositReturn')
                )}
                ref={ this.refs.securityDepositReturn } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Name of Property Management Office</label>
              <input type="text" placeholder="Property management office"
                className={ cx('form-control', {
                  'form-control': true,
                  'is-invalid': cxValidateField(form.fields.propertyManagementOffice)
                })}
                value={ document.propertyManagementOffice }
                onChange={ handlers(
                  this.linkToDocument('propertyManagementOffice'),
                  hopplerLinkState(form$, form, 'propertyManagementOffice')
                )}
                ref={ this.refs.propertyManagementOffice } />
            </div>
            <div className="hi-input-group">
              <label className="hi-input-group__label">Name/Location of Court Handling Disputes</label>
              <input type="text" placeholder="Court name or location"
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields.courtHandlingDisputes)
                })}
                value={ document.courtHandlingDisputes }
                onChange={ handlers(
                  this.linkToDocument('courtHandlingDisputes'),
                  hopplerLinkState(form$, form, 'courtHandlingDisputes')
                )}
                ref={ this.refs.courtHandlingDisputes } />
            </div>
          </div>
        )}

        <div className="hi-doctermsform__button-row">
          <button className="btn btn-outline-dark hi-doctermsform__print-button"
            disabled={
              !(props.document.loiKey || props.document.contractKey) ||
                props.saveState === ReqState.IN_PROGRESS
            }
            onClick={ () => this.onPrintClick() }>
            Print
          </button>
          <button className="btn btn-secondary hi-doctermsform__save-button"
            disabled={ props.saveState === ReqState.IN_PROGRESS }
            onClick={ () => this.onSaveClick() }>
            { props.saveState === ReqState.IN_PROGRESS ? <i className="fas fa-cog fa-spin" /> : 'Save' }
          </button>
        </div>
      </div>
    );
  }

  render(props: InqDocumentTermsFormProps, state: InqDocumentTermsFormState) {
    return (
      <div className="hi-docmodal__tabbed-container">
        <InqDocumentModalTabs activeTab={ props.activeTab }
          onTabClick={ (tabId) => this.onTabClick(tabId) } />
        
        <div className="hi-docmodal__tab-content" ref={ this.formScrollerRef }>
          { this.renderForm(props, state) }
        </div>
      </div>
    )
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}